import React, { useState, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './quillButton';
import './quillIframe';
import { uploadFile, fetchData, sendData, updateData } from '../../../_metronic/helpers/utils/api';
import { cmsPrompt, cmsAlert } from '../../components/CmsModal';

export type BlockType = 'heroRightImage' | 'heroCentered' | 'logoCarousel' | 'wysiwyg' | 'galleryCarousel' | 'featureCards' | 'team';
import carouselPattern from '../../../_metronic/assets/carouselPattern.svg';

export interface BlockData {
  id: string;
  type: BlockType;
  title?: string;
  subtitle?: string;
  imageUrl?: string;
  videoUrl?: string;
  buttonText?: string;
  buttonLink?: string;
  content?: string;
  logos?: string;
  logoItems?: { image: string; link: string }[];
  galleryUrls?: string;
  galleryItems?: { image: string; caption?: string; link?: string }[];
  cards?: {
    id: string;
    title: string;
    description: string;
    imageUrl: string;
    backgroundColor: string;
    textColor?: 'white' | 'black';
    links: { text: string; url: string }[];
  }[];
  teamItems?: { name: string; designation: string; image: string; link: string; bio?: string }[];
}

interface PageBuilderProps {
  pageId: string;
  slug?: string;
}

const AVAILABLE_BLOCKS: { type: BlockType; label: string; icon: string | React.ReactNode }[] = [
  { type: 'heroRightImage', label: 'Hero (Right Image)', icon: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary me-4 flex-shrink-0">
      <rect x="1" y="5" width="8" height="14" rx="1.5"></rect>
      <line x1="3.5" y1="8.5" x2="6.5" y2="8.5"></line>
      <line x1="5" y1="8.5" x2="5" y2="13"></line>
      <line x1="3.5" y1="15.5" x2="6.5" y2="15.5"></line>
      
      <rect x="12" y="5" width="11" height="14" rx="1.5"></rect>
      <path d="M12 19 L15.5 13.5 L18 16.5"></path>
      <path d="M16.5 14.7 L19.5 10 L23 19"></path>
    </svg>
  ) },
  { type: 'heroCentered', label: 'Hero (Centered)', icon: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary me-4 flex-shrink-0">
      <rect x="2" y="2" width="20" height="9" rx="1.5"></rect>
      <line x1="9" y1="4.5" x2="15" y2="4.5"></line>
      <line x1="12" y1="4.5" x2="12" y2="8.5"></line>
      
      <rect x="2" y="13" width="20" height="9" rx="1.5"></rect>
      <path d="M2 22 L7 16 L11 20"></path>
      <path d="M9 18 L14 15 L22 22"></path>
    </svg>
  ) },
  { type: 'wysiwyg', label: 'Rich Text', icon: 'bi-file-earmark-richtext' },
  { type: 'logoCarousel', label: 'Logo Carousel', icon: 'bi-images' },
  { type: 'galleryCarousel', label: 'Image Gallery', icon: 'bi-collection' },
  { type: 'featureCards', label: 'Feature Cards', icon: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary me-4 flex-shrink-0">
      <rect x="2" y="6" width="5" height="12" rx="1"></rect>
      <rect x="9.5" y="6" width="5" height="12" rx="1"></rect>
      <rect x="17" y="6" width="5" height="12" rx="1"></rect>
    </svg>
  ) },
  { type: 'team', label: 'Team Members', icon: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary me-4 flex-shrink-0">
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M2 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2"></path>
      <circle cx="18" cy="9" r="3"></circle>
      <path d="M22 21v-2a3 3 0 0 0-3-3h-1.5"></path>
    </svg>
  ) },
];

const QUILL_MODULES = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link', 'image', 'video', 'actionButton', 'iframeEmbed'],
      ['clean'],
    ],
    handlers: {
      actionButton: function(this: any) {
        try {
          const quill = this.quill;
          if (!quill) {
            console.error('Quill instance not found in handler context');
            return;
          }
          
          cmsPrompt({
            title: 'Insert Action Button',
            description: 'Add a styled call-to-action button into your content.',
            icon: 'bi bi-link-45deg',
            fields: [
              { id: 'url', label: 'Button URL', placeholder: 'e.g. /contact or https://example.com', type: 'text', required: true },
              { id: 'text', label: 'Button Text', placeholder: 'e.g. Learn More', type: 'text', required: true },
              { id: 'newTab', label: 'Open in new tab', type: 'toggle', defaultValue: 'true' },
            ],
            submitText: 'Insert Button',
          }).then((values) => {
            if (!values) return;
            const { url, text, newTab } = values;
            if (!url || !text) return;
            
            let range = quill.getSelection(true);
            if (!range) {
              range = { index: quill.getLength() || 0, length: 0 };
            }
            
            const target = newTab === 'true' ? '_blank' : '_self';
            quill.insertEmbed(range.index, 'actionButton', { url, text, target }, 'user');
            quill.setSelection(range.index + 1, 'silent');
          });
        } catch (e) {
          console.error('Failed to insert action button', e);
        }
      },
      iframeEmbed: function(this: any) {
        try {
          const quill = this.quill;
          if (!quill) {
            console.error('Quill instance not found in handler context');
            return;
          }
          
          cmsPrompt({
            title: 'Embed Content',
            description: 'Paste an iframe URL to embed external content (forms, maps, videos, etc.).',
            icon: 'bi bi-code-slash',
            fields: [
              { id: 'src', label: 'Embed URL', placeholder: 'e.g. https://www.youtube.com/embed/... or https://forms.google.com/...', type: 'text', required: true },
              { id: 'height', label: 'Height (px)', placeholder: '500', type: 'text', defaultValue: '500' },
            ],
            submitText: 'Insert Embed',
          }).then((values) => {
            if (!values) return;
            const { src, height } = values;
            if (!src) return;
            
            let range = quill.getSelection(true);
            if (!range) {
              range = { index: quill.getLength() || 0, length: 0 };
            }
            
            quill.insertEmbed(range.index, 'iframeEmbed', { src, height: height || '500' }, 'user');
            quill.setSelection(range.index + 1, 'silent');
          });
        } catch (e) {
          console.error('Failed to insert iframe embed', e);
        }
      }
    }
  },
  clipboard: {
    matchVisual: false, // Prevents Word from adding extra blank lines and normalizes paste
  }
};

const QUILL_FORMATS = [
  'header',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link', 'image', 'video',
  'actionButton',
  'iframeEmbed'
];

export const PageBuilder: React.FC<PageBuilderProps> = ({ pageId, slug }) => {
  const [blocks, setBlocks] = useState<BlockData[]>([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [expandedBlockIds, setExpandedBlockIds] = useState<string[]>([]);



  const [dataId, setDataId] = useState<number | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const loadBlocks = async () => {
      try {
        const existingData = await fetchData({
          pageId: Number(pageId),
          sectionName: 'CustomPageBlocks',
          key: 'blocks'
        })();
        
        if (existingData && existingData.valueEnglish) {
          setDataId(existingData.id);
          let fetchedBlocks = existingData.valueEnglish;
          if (typeof fetchedBlocks === 'string') {
            try {
              fetchedBlocks = JSON.parse(fetchedBlocks);
            } catch(e) {}
          }
          if (Array.isArray(fetchedBlocks)) {
            setBlocks(fetchedBlocks as BlockData[]);
          } else {
            setBlocks([]);
          }
        } else {
          setDataId(null);
          setBlocks([]);
        }
      } catch (e) {
        console.error('Failed to load blocks from API', e);
        setDataId(null);
        setBlocks([]);
      }
    };
    loadBlocks();
  }, [pageId]);

  useEffect(() => {
    // Add simple tooltips to Quill toolbar
    const tooltips: Record<string, string> = {
      '.ql-bold': 'Bold',
      '.ql-italic': 'Italic',
      '.ql-underline': 'Underline',
      '.ql-strike': 'Strikethrough',
      '.ql-blockquote': 'Blockquote',
      '.ql-list[value="ordered"]': 'Numbered List',
      '.ql-list[value="bullet"]': 'Bulleted List',
      '.ql-indent[value="-1"]': 'Decrease Indent',
      '.ql-indent[value="+1"]': 'Increase Indent',
      '.ql-link': 'Insert Link',
      '.ql-image': 'Insert Image',
      '.ql-video': 'Insert Video',
      '.ql-clean': 'Clear Formatting',
      '.ql-actionButton': 'Insert Action Button',
      '.ql-header': 'Heading Style',
    };

    // Use an interval to wait for the DOM elements to be fully rendered
    let attempts = 0;
    const interval = setInterval(() => {
      let elementsFound = false;
      Object.entries(tooltips).forEach(([selector, text]) => {
        document.querySelectorAll(selector).forEach((el) => {
          if (!el.hasAttribute('title')) {
            (el as HTMLElement).setAttribute('title', text);
          }
          elementsFound = true;
        });
      });
      
      attempts++;
      // Stop checking after 20 attempts (2 seconds) to avoid infinite loop
      if (attempts > 20) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [blocks, expandedBlockIds]);

  const saveBlocks = async (newBlocks: BlockData[]) => {
    setBlocks(newBlocks);
    setSaveStatus('saving');
    
    try {
      const body = {
        pageId: Number(pageId),
        sectionName: 'CustomPageBlocks',
        key: 'blocks',
        valueEnglish: newBlocks
      };
      if (dataId) {
        await updateData({ id: dataId, body });
      } else {
        const res = await sendData(body);
        if (res && res.id) setDataId(res.id);
      }
      
      setSaveStatus('saved');
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
      
    } catch (e) {
      console.error('Failed to save blocks to API', e);
      setSaveStatus('idle');
    }
  };

  const addBlock = (type: BlockType) => {
    const newBlock: BlockData = { id: Date.now().toString(), type };
    if (type === 'heroRightImage' || type === 'heroCentered') {
      newBlock.title = 'New Hero Title';
      newBlock.subtitle = 'This is a subtitle for the hero section.';
      newBlock.imageUrl = '';
      newBlock.videoUrl = '';
      newBlock.buttonText = '';
      newBlock.buttonLink = '';
    } else if (type === 'wysiwyg') {
      newBlock.content = '<p>Write your content here...</p>';
    } else if (type === 'logoCarousel') {
      newBlock.logoItems = [];
      newBlock.logos = ''; // keeping for backwards compatibility if needed
    } else if (type === 'galleryCarousel') {
      newBlock.galleryUrls = '';
      newBlock.galleryItems = [];
    }
    const newBlocks = [...blocks, newBlock];
    setExpandedBlockIds(prev => [...prev, newBlock.id]);
    saveBlocks(newBlocks);
  };

  const removeBlock = (id: string) => {
    saveBlocks(blocks.filter((b) => b.id !== id));
  };

  const updateBlock = (id: string, updates: Partial<BlockData>) => {
    saveBlocks(blocks.map((b) => (b.id === id ? { ...b, ...updates } : b)));
  };

  const moveBlock = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index > 0) {
      const newBlocks = [...blocks];
      const temp = newBlocks[index - 1];
      newBlocks[index - 1] = newBlocks[index];
      newBlocks[index] = temp;
      saveBlocks(newBlocks);
    } else if (direction === 'down' && index < blocks.length - 1) {
      const newBlocks = [...blocks];
      const temp = newBlocks[index + 1];
      newBlocks[index + 1] = newBlocks[index];
      newBlocks[index] = temp;
      saveBlocks(newBlocks);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, callback: (url: string) => void) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const url = await uploadFile(file);
        if (url) {
          callback(url);
        } else {
          console.error('Upload failed, no URL returned');
          cmsAlert({ title: 'Upload Failed', description: 'Failed to upload image. Please try again.', variant: 'danger', icon: 'bi bi-exclamation-triangle' });
        }
      } catch (err) {
        console.error('Error uploading file:', err);
        cmsAlert({ title: 'Upload Failed', description: 'Failed to upload image. Please try again.', variant: 'danger', icon: 'bi bi-exclamation-triangle' });
      }
    }
  };

  const handleMultipleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, callback: (urls: string[]) => void) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      try {
        const uploadPromises = Array.from(files).map(file => uploadFile(file));
        const urls = await Promise.all(uploadPromises);
        const validUrls = urls.filter(Boolean) as string[];
        if (validUrls.length > 0) {
          callback(validUrls);
        }
      } catch (err) {
        console.error('Error uploading files:', err);
        cmsAlert({ title: 'Upload Failed', description: 'Failed to upload images.', variant: 'danger', icon: 'bi bi-exclamation-triangle' });
      }
    }
  };

  return (
    <div className="d-flex w-100 h-100" style={{ gap: '30px', alignItems: 'flex-start' }}>
      {/* Sidebar */}
      {!isPreviewMode && (
        <div className="card shadow-sm" style={{ width: '280px', flexShrink: 0, position: 'sticky', top: '100px' }}>
          <div className="card-header pt-7 pb-4 border-0">
            <h3 className="card-title fw-bolder text-dark m-0 fs-4">Components</h3>
          </div>
          <div className="card-body pt-3">
            <p className="text-muted fs-7 mb-6">Click to add a section to your page.</p>
            <div className="d-flex flex-column gap-3">
              {AVAILABLE_BLOCKS.map((b) => (
                <button
                  key={b.type}
                  className="btn btn-outline btn-outline-dashed btn-outline-default d-flex align-items-center justify-content-start text-start p-4 w-100"
                  onClick={() => addBlock(b.type)}
                >
                  {typeof b.icon === 'string' ? (
                    <i className={`bi ${b.icon} fs-2x me-4 text-primary`}></i>
                  ) : (
                    b.icon
                  )}
                  <span className="fw-bolder text-gray-800 d-block fs-6">{b.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Canvas */}
      <div className="flex-grow-1 d-flex flex-column gap-5 w-100">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center">
            {saveStatus === 'saving' && (
              <span className="badge badge-light-warning fs-8 py-2 px-3 d-flex align-items-center gap-2">
                <span className="spinner-border spinner-border-sm w-15px h-15px align-middle"></span>
                Saving changes...
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="badge badge-light-success fs-8 py-2 px-3 d-flex align-items-center gap-2">
                <i className="bi bi-check-circle-fill text-success fs-7"></i>
                All changes saved
              </span>
            )}
          </div>
          <button 
            className={`btn btn-sm ${isPreviewMode ? 'btn-primary' : 'btn-light-primary'}`}
            onClick={() => setIsPreviewMode(!isPreviewMode)}
          >
            <i className={`bi ${isPreviewMode ? 'bi-eye-slash' : 'bi-eye'} me-2`}></i>
            {isPreviewMode ? 'Exit Inline Preview' : 'Inline Preview'}
          </button>
        </div>
        {blocks.length === 0 ? (
          <div className="card shadow-sm">
            <div className="card-body p-15 text-center">
              <i className="bi bi-layout-text-window text-muted fs-5x mb-5"></i>
              <h4 className="text-gray-800 fw-bolder mb-3">No Components Yet</h4>
              <p className="text-muted fs-6 mb-0">Select a component from the sidebar to start building your page.</p>
            </div>
          </div>
        ) : (
          blocks.map((block, index) => {
            const isExpanded = isPreviewMode || expandedBlockIds.includes(block.id);
            const blockLabel = AVAILABLE_BLOCKS.find(b => b.type === block.type)?.label || 'Component';
            
            return (
            <div key={block.id} className={`card shadow-sm border border-light position-relative ${isPreviewMode ? 'border-0 shadow-none' : ''}`}>
              {/* Block Actions */}
              {!isPreviewMode && (
                <div 
                  className="card-header border-0 min-h-50px px-6 py-2 d-flex align-items-center justify-content-between cursor-pointer" 
                  style={{ backgroundColor: '#f9f9f9', borderTopLeftRadius: '0.475rem', borderTopRightRadius: '0.475rem', borderBottom: isExpanded ? '1px solid #EFF2F5' : 'none' }} 
                  onClick={() => {
                    if (isExpanded) {
                      setExpandedBlockIds(prev => prev.filter(id => id !== block.id));
                    } else {
                      setExpandedBlockIds(prev => [...prev, block.id]);
                    }
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} text-muted`}></i>
                    <span className="fw-bolder text-gray-800">{blockLabel}</span>
                  </div>
                  <div className="d-flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn btn-icon btn-sm btn-light-primary"
                      onClick={() => moveBlock(index, 'up')}
                      disabled={index === 0}
                      title="Move Up"
                    >
                      <i className="bi bi-arrow-up"></i>
                    </button>
                    <button
                      className="btn btn-icon btn-sm btn-light-primary"
                      onClick={() => moveBlock(index, 'down')}
                      disabled={index === blocks.length - 1}
                      title="Move Down"
                    >
                      <i className="bi bi-arrow-down"></i>
                    </button>
                    <button
                      className="btn btn-icon btn-sm btn-light-danger ms-2"
                      onClick={() => removeBlock(block.id)}
                      title="Remove Block"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              )}

              {isExpanded && (
                <div className={`card-body ${isPreviewMode ? 'p-0' : 'p-8 pt-10'}`}>
                {block.type === 'heroRightImage' && (
                  <div className={`row align-items-center ${isPreviewMode ? 'bg-dark text-white rounded p-10 m-0' : 'g-5'}`} style={isPreviewMode ? { backgroundColor: '#121E5C' } : {}}>
                    <div className="col-lg-6 d-flex flex-column gap-4">
                      {isPreviewMode ? (
                        <>
                          <h1 className="display-4 fw-bolder mb-0 text-white">{block.title}</h1>
                          <p className="fs-4 opacity-75 mb-0 text-white">{block.subtitle}</p>
                          {block.buttonText && (
                            <a href={block.buttonLink || '#'} className="btn btn-warning fw-bold px-6 py-3 align-self-start" target={block.buttonLink?.startsWith('http') ? '_blank' : '_self'} rel="noreferrer">
                              {block.buttonText}
                            </a>
                          )}
                        </>
                      ) : (
                        <>
                          <input
                            type="text"
                            className="form-control form-control-solid fs-2x fw-bolder"
                            value={block.title || ''}
                            onChange={(e) => updateBlock(block.id, { title: e.target.value })}
                            placeholder="Hero Title"
                          />
                          <textarea
                            className="form-control form-control-solid fs-5"
                            rows={3}
                            value={block.subtitle || ''}
                            onChange={(e) => updateBlock(block.id, { subtitle: e.target.value })}
                            placeholder="Hero Subtitle"
                          />
                          <div className="border border-dashed border-gray-300 rounded p-4 bg-light-primary bg-opacity-25">
                            <label className="fw-bold fs-7 mb-2 d-block"><i className="bi bi-link-45deg me-1"></i>Call-to-Action Button <span className="text-muted fw-normal">(optional)</span></label>
                            <div className="d-flex gap-3">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Button text (leave blank to hide)"
                                value={block.buttonText || ''}
                                onChange={(e) => updateBlock(block.id, { buttonText: e.target.value })}
                              />
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Button link (e.g. /register or https://...)"
                                value={block.buttonLink || ''}
                                onChange={(e) => updateBlock(block.id, { buttonLink: e.target.value })}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="col-lg-6">
                      {isPreviewMode ? (
                        block.videoUrl ? (
                          <div className="d-flex justify-content-center w-100">
                            <div className="ratio ratio-16x9 rounded shadow overflow-hidden w-100">
                              <iframe src={block.videoUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')} title="Video" allowFullScreen></iframe>
                            </div>
                          </div>
                        ) : block.imageUrl ? (
                          <div className="d-flex justify-content-center w-100">
                            <img src={block.imageUrl} alt="Hero" className="img-fluid rounded shadow" style={{ maxHeight: '400px', objectFit: 'cover' }} />
                          </div>
                        ) : null
                      ) : (
                        <div className="border border-dashed border-gray-300 rounded d-flex flex-column align-items-center justify-content-center p-5 bg-light" style={{ minHeight: '200px' }}>
                          {block.videoUrl ? (
                            <div className="ratio ratio-16x9 rounded overflow-hidden w-100 mb-3">
                              <iframe src={block.videoUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')} title="Video Preview" allowFullScreen></iframe>
                            </div>
                          ) : block.imageUrl ? (
                            <img src={block.imageUrl} alt="Hero" className="img-fluid rounded mw-100 h-auto" style={{ maxHeight: '200px' }} />
                          ) : (
                            <i className="bi bi-image text-muted fs-3x mb-3"></i>
                          )}
                          <div className="d-flex w-100 gap-2 mt-4">
                            <input
                              type="text"
                              className="form-control form-control-sm form-control-solid flex-grow-1"
                              placeholder="Image URL"
                              value={block.imageUrl || ''}
                              onChange={(e) => updateBlock(block.id, { imageUrl: e.target.value })}
                            />
                            <div className="position-relative overflow-hidden btn btn-sm btn-light-primary text-nowrap">
                              Upload
                              <input 
                                type="file" 
                                className="position-absolute top-0 end-0 opacity-0 cursor-pointer h-100 w-100" 
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, (url) => updateBlock(block.id, { imageUrl: url }))}
                              />
                            </div>
                          </div>
                          <div className="w-100 mt-3">
                            <label className="fw-bold fs-7 mb-2 d-block"><i className="bi bi-youtube text-danger me-1"></i>YouTube Video URL <span className="text-muted fw-normal">(overrides image when set)</span></label>
                            <input
                              type="text"
                              className="form-control form-control-sm form-control-solid"
                              placeholder="e.g. https://www.youtube.com/watch?v=... or https://youtu.be/..."
                              value={block.videoUrl || ''}
                              onChange={(e) => updateBlock(block.id, { videoUrl: e.target.value })}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {block.type === 'heroCentered' && (
                  <div 
                    className={`d-flex flex-column align-items-center text-center w-100 gap-4 ${isPreviewMode ? 'rounded shadow-sm overflow-hidden' : ''}`}
                  >
                    
                    <div className="w-100 d-flex flex-column align-items-center gap-4">
                      {isPreviewMode ? (
                        block.videoUrl ? (
                          <div className="position-relative w-100 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                            <div className="ratio ratio-16x9 w-100 rounded overflow-hidden">
                              <iframe src={block.videoUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')} title="Video" allowFullScreen></iframe>
                            </div>
                            {(block.title || block.subtitle) && (
                              <div className="mt-5 d-flex flex-column gap-3" style={{ maxWidth: '800px' }}>
                                {block.title && <h1 className="display-4 fw-bolder mb-2 text-dark">{block.title}</h1>}
                                {block.subtitle && <p className="fs-3 text-muted mb-0">{block.subtitle}</p>}
                                {block.buttonText && (
                                  <a href={block.buttonLink || '#'} className="btn btn-primary fw-bold px-6 py-3 align-self-center" target={block.buttonLink?.startsWith('http') ? '_blank' : '_self'} rel="noreferrer">
                                    {block.buttonText}
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        ) : block.imageUrl ? (
                          <div className="position-relative w-100 d-flex flex-column align-items-center justify-content-center py-20 px-10" style={{ minHeight: '400px' }}>
                            <div className="position-absolute top-0 start-0 w-100 h-100" style={{ zIndex: 0 }}>
                              <img src={block.imageUrl} alt="Hero" className="w-100 h-100" style={{ objectFit: 'cover' }} />
                              <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"></div>
                            </div>
                            <div className="position-relative d-flex flex-column gap-3" style={{ zIndex: 1, maxWidth: '800px' }}>
                              <h1 className="display-4 fw-bolder mb-2 text-white">{block.title}</h1>
                              <p className="fs-3 text-white text-opacity-75 mb-0">{block.subtitle}</p>
                              {block.buttonText && (
                                <a href={block.buttonLink || '#'} className="btn btn-warning fw-bold px-6 py-3 align-self-center" target={block.buttonLink?.startsWith('http') ? '_blank' : '_self'} rel="noreferrer">
                                  {block.buttonText}
                                </a>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="w-100 d-flex flex-column align-items-center justify-content-center py-10 px-10 rounded" style={{ backgroundImage: `url(${carouselPattern}), linear-gradient(135deg, #121E5C 0%, #121E5C 100%)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                            <div className="d-flex flex-column gap-3" style={{ maxWidth: '800px' }}>
                              <h1 className="display-4 fw-bolder mb-2 text-white">{block.title}</h1>
                              <p className="fs-3 text-white text-opacity-75 mb-0">{block.subtitle}</p>
                              {block.buttonText && (
                                <a href={block.buttonLink || '#'} className="btn btn-warning fw-bold px-6 py-3 align-self-center" target={block.buttonLink?.startsWith('http') ? '_blank' : '_self'} rel="noreferrer">
                                  {block.buttonText}
                                </a>
                              )}
                            </div>
                          </div>
                        )
                      ) : (
                        <>
                          <input
                            type="text"
                            className="form-control form-control-solid fs-1 text-center fw-bolder w-75"
                            value={block.title || ''}
                            onChange={(e) => updateBlock(block.id, { title: e.target.value })}
                            placeholder="Centered Hero Title"
                          />
                          <textarea
                            className="form-control form-control-solid fs-4 text-center w-50"
                            rows={2}
                            value={block.subtitle || ''}
                            onChange={(e) => updateBlock(block.id, { subtitle: e.target.value })}
                            placeholder="Hero Subtitle"
                          />
                          <div className="border border-dashed border-gray-300 rounded p-4 bg-light-primary bg-opacity-25 w-75">
                            <label className="fw-bold fs-7 mb-2 d-block"><i className="bi bi-link-45deg me-1"></i>Call-to-Action Button <span className="text-muted fw-normal">(optional)</span></label>
                            <div className="d-flex gap-3">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Button text (leave blank to hide)"
                                value={block.buttonText || ''}
                                onChange={(e) => updateBlock(block.id, { buttonText: e.target.value })}
                              />
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Button link (e.g. /register or https://...)"
                                value={block.buttonLink || ''}
                                onChange={(e) => updateBlock(block.id, { buttonLink: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="border border-dashed border-gray-300 rounded d-flex flex-column align-items-center justify-content-center p-5 bg-light mt-2 w-100" style={{ minHeight: '200px' }}>
                            {block.videoUrl ? (
                              <div className="ratio ratio-16x9 rounded overflow-hidden w-75 mb-3">
                                <iframe src={block.videoUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')} title="Video Preview" allowFullScreen></iframe>
                              </div>
                            ) : block.imageUrl ? (
                              <img src={block.imageUrl} alt="Background" className="img-fluid rounded mw-100 h-auto" style={{ maxHeight: '200px' }} />
                            ) : (
                              <i className="bi bi-image text-muted fs-3x mb-3"></i>
                            )}
                            <div className="d-flex w-50 gap-2 mt-4">
                              <input
                                type="text"
                                className="form-control form-control-sm form-control-solid flex-grow-1"
                                placeholder="Background Image URL"
                                value={block.imageUrl || ''}
                                onChange={(e) => updateBlock(block.id, { imageUrl: e.target.value })}
                              />
                              <div className="position-relative overflow-hidden btn btn-sm btn-light-primary text-nowrap">
                                Upload
                                <input 
                                  type="file" 
                                  className="position-absolute top-0 end-0 opacity-0 cursor-pointer h-100 w-100" 
                                  accept="image/*"
                                  onChange={(e) => handleImageUpload(e, (url) => updateBlock(block.id, { imageUrl: url }))}
                                />
                              </div>
                            </div>
                            <div className="w-100 mt-3">
                              <label className="fw-bold fs-7 mb-2 d-block text-start"><i className="bi bi-youtube text-danger me-1"></i>YouTube Video URL <span className="text-muted fw-normal">(overrides image when set)</span></label>
                              <input
                                type="text"
                                className="form-control form-control-sm form-control-solid"
                                placeholder="e.g. https://www.youtube.com/watch?v=... or https://youtu.be/..."
                                value={block.videoUrl || ''}
                                onChange={(e) => updateBlock(block.id, { videoUrl: e.target.value })}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {block.type === 'logoCarousel' && (
                  <div className={`d-flex flex-column ${isPreviewMode ? 'align-items-center' : 'gap-3'}`}>
                    {isPreviewMode ? (
                      <>
                        {(block.title || block.subtitle) && (
                          <div className="text-center mb-4">
                            {block.title && <h3 className="fw-bolder text-dark mb-1">{block.title}</h3>}
                            {block.subtitle && <p className="text-muted fs-6">{block.subtitle}</p>}
                          </div>
                        )}
                        <div className="d-flex flex-wrap justify-content-center gap-5 my-5">
                        {block.logoItems && block.logoItems.length > 0 ? (
                          block.logoItems.map((item, idx) => (
                            <a key={idx} href={item.link || '#'} target="_blank" rel="noreferrer" style={{ pointerEvents: item.link ? 'auto' : 'none' }}>
                              <img src={item.image} alt="Logo" style={{ height: '60px', objectFit: 'contain' }} />
                            </a>
                          ))
                        ) : (
                          block.logos ? block.logos.split(',').map((l, idx) => l.trim() && (
                            <img key={idx} src={l.trim()} alt="Logo" style={{ height: '60px', objectFit: 'contain' }} />
                          )) : null
                        )}
                      </div>
                      </>
                    ) : (
                      <>
                        <div className="d-flex gap-3 mb-4">
                          <div className="flex-grow-1">
                            <label className="form-label fw-bold fs-7 text-gray-700">Section Title (Optional)</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g. Our Partners"
                              value={block.title || ''}
                              onChange={(e) => updateBlock(block.id, { title: e.target.value })}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <label className="form-label fw-bold fs-7 text-gray-700">Section Subtitle (Optional)</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g. Trusted by leading organizations"
                              value={block.subtitle || ''}
                              onChange={(e) => updateBlock(block.id, { subtitle: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="text-center">
                          <h4 className="text-gray-800 fw-bolder mb-3">Partner Logos</h4>
                          <i className="bi bi-images text-muted fs-1 mb-4 d-block"></i>
                        </div>
                        
                        {/* Legacy support message if logos exist but logoItems is empty */}
                        {block.logos && (!block.logoItems || block.logoItems.length === 0) && (
                          <div className="alert alert-warning d-flex align-items-center p-3 mb-4">
                            <i className="bi bi-exclamation-triangle fs-3 text-warning me-3"></i>
                            <div className="d-flex flex-column">
                              <span className="fw-bolder">Legacy Logos Detected</span>
                              <span>Please re-add your logos below to support clickable links.</span>
                            </div>
                          </div>
                        )}

                        <div className="d-flex flex-column gap-3 mb-4">
                          {(block.logoItems || []).map((item, idx) => (
                            <div key={idx} className="d-flex gap-3 align-items-center bg-light p-3 rounded border">
                              <img src={item.image} alt="Logo" style={{ width: '60px', height: '60px', objectFit: 'contain', backgroundColor: 'white' }} className="rounded border p-1" />
                              <div className="d-flex flex-column flex-grow-1 gap-2">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Image URL"
                                  value={item.image}
                                  onChange={(e) => {
                                    const newItems = [...(block.logoItems || [])];
                                    newItems[idx].image = e.target.value;
                                    updateBlock(block.id, { logoItems: newItems });
                                  }}
                                />
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Link URL (Optional, e.g. https://partner.com)"
                                  value={item.link || ''}
                                  onChange={(e) => {
                                    const newItems = [...(block.logoItems || [])];
                                    newItems[idx].link = e.target.value;
                                    updateBlock(block.id, { logoItems: newItems });
                                  }}
                                />
                              </div>
                              <button 
                                className="btn btn-icon btn-sm btn-light-danger"
                                onClick={() => {
                                  const newItems = [...(block.logoItems || [])];
                                  newItems.splice(idx, 1);
                                  updateBlock(block.id, { logoItems: newItems });
                                }}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          ))}
                        </div>

                        <div className="d-flex justify-content-center align-items-center gap-2">
                          <div className="position-relative overflow-hidden btn btn-sm btn-light-primary">
                            Upload Logo & Add
                            <input 
                              type="file" 
                              className="position-absolute top-0 end-0 opacity-0 cursor-pointer h-100 w-100" 
                              accept="image/*"
                              onChange={(e) => {
                                handleImageUpload(e, (url) => {
                                  const newItems = [...(block.logoItems || [])];
                                  newItems.push({ image: url, link: '' });
                                  updateBlock(block.id, { logoItems: newItems });
                                });
                                e.target.value = ''; // Reset file input
                              }}
                            />
                          </div>
                          <button 
                            className="btn btn-sm btn-light"
                            onClick={() => {
                              const newItems = [...(block.logoItems || [])];
                              newItems.push({ image: '', link: '' });
                              updateBlock(block.id, { logoItems: newItems });
                            }}
                          >
                            Add from URL
                          </button>
                        </div>
                        <div className="text-muted fs-7 mt-3 text-center">
                          These logos will automatically rotate in a carousel. You can add an optional link so users can click on them.
                        </div>
                      </>
                    )}
                  </div>
                )}

                {block.type === 'galleryCarousel' && (
                  <div className={`d-flex flex-column ${isPreviewMode ? 'align-items-center' : 'gap-3'}`}>
                    {isPreviewMode ? (
                      <>
                        {(block.title || block.subtitle) && (
                          <div className="text-center mb-4 w-100">
                            {block.title && <h2 className="fw-bolder text-dark fs-1 mb-2">{block.title}</h2>}
                            {block.subtitle && <p className="text-muted fs-5 mb-0">{block.subtitle}</p>}
                          </div>
                        )}
                        <div className="d-flex w-100 overflow-auto gap-4 my-5 px-5" style={{ scrollSnapType: 'x mandatory' }}>
                        {block.galleryItems && block.galleryItems.length > 0 ? block.galleryItems.map((item, idx) => (
                          <div key={idx} style={{ scrollSnapAlign: 'center', flexShrink: 0, width: '80%', maxWidth: '600px' }} className="position-relative">
                            <img src={item.image} alt="Gallery" className="img-fluid rounded shadow" style={{ height: '300px', width: '100%', objectFit: 'cover' }} />
                            {item.caption && (
                               <div className="position-absolute bottom-0 start-0 w-100 p-3 bg-dark bg-opacity-50 text-white rounded-bottom">
                                 {item.caption}
                               </div>
                            )}
                          </div>
                        )) : block.galleryUrls ? block.galleryUrls.split(',').map((l, idx) => l.trim() && (
                          <div key={idx} style={{ scrollSnapAlign: 'center', flexShrink: 0, width: '80%', maxWidth: '600px' }}>
                            <img src={l.trim()} alt="Gallery" className="img-fluid rounded shadow" style={{ height: '300px', width: '100%', objectFit: 'cover' }} />
                          </div>
                        )) : <p className="text-muted w-100 text-center py-10">No images added to gallery.</p>}
                      </div>
                      </>
                    ) : (
                      <>
                        <div className="d-flex flex-column gap-3 mb-4">
                          <input
                            type="text"
                            className="form-control form-control-lg fw-bold text-center"
                            placeholder="Gallery Title (optional)"
                            value={block.title || ''}
                            onChange={(e) => updateBlock(block.id, { title: e.target.value })}
                          />
                          <input
                            type="text"
                            className="form-control text-center"
                            placeholder="Gallery Subtitle (optional)"
                            value={block.subtitle || ''}
                            onChange={(e) => updateBlock(block.id, { subtitle: e.target.value })}
                          />
                        </div>
                        <div className="text-center">
                          <h4 className="text-gray-800 fw-bolder mb-3">Image Gallery</h4>
                          <i className="bi bi-collection-play text-muted fs-1 mb-4 d-block"></i>
                        </div>

                        {/* Legacy support message if urls exist but items is empty */}
                        {block.galleryUrls && (!block.galleryItems || block.galleryItems.length === 0) && (
                          <div className="alert alert-warning d-flex align-items-center p-3 mb-4">
                            <i className="bi bi-exclamation-triangle fs-3 text-warning me-3"></i>
                            <div className="d-flex flex-column">
                              <span className="fw-bolder">Legacy Gallery Detected</span>
                              <span>Please re-add your images below to support captions.</span>
                            </div>
                          </div>
                        )}

                        <div className="d-flex flex-column gap-3 mb-4">
                          {(block.galleryItems || []).map((item, idx) => (
                            <div key={idx} className="d-flex gap-3 align-items-center bg-light p-3 rounded border">
                              <div className="d-flex flex-column gap-1 align-items-center" style={{ minWidth: '28px' }}>
                                <button
                                  className="btn btn-icon btn-sm btn-light p-0"
                                  style={{ width: '24px', height: '24px' }}
                                  disabled={idx === 0}
                                  title="Move Up"
                                  onClick={() => {
                                    const newItems = [...(block.galleryItems || [])];
                                    const temp = newItems[idx - 1];
                                    newItems[idx - 1] = newItems[idx];
                                    newItems[idx] = temp;
                                    updateBlock(block.id, { galleryItems: newItems });
                                  }}
                                >
                                  <i className="bi bi-chevron-up fs-8"></i>
                                </button>
                                <span className="badge badge-circle badge-light-primary fs-9">{idx + 1}</span>
                                <button
                                  className="btn btn-icon btn-sm btn-light p-0"
                                  style={{ width: '24px', height: '24px' }}
                                  disabled={idx === (block.galleryItems || []).length - 1}
                                  title="Move Down"
                                  onClick={() => {
                                    const newItems = [...(block.galleryItems || [])];
                                    const temp = newItems[idx + 1];
                                    newItems[idx + 1] = newItems[idx];
                                    newItems[idx] = temp;
                                    updateBlock(block.id, { galleryItems: newItems });
                                  }}
                                >
                                  <i className="bi bi-chevron-down fs-8"></i>
                                </button>
                              </div>
                              <img src={item.image} alt="Gallery" style={{ width: '100px', height: '60px', objectFit: 'cover', backgroundColor: 'white' }} className="rounded border p-1" />
                              <div className="d-flex flex-column flex-grow-1 gap-2">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Image URL"
                                  value={item.image}
                                  onChange={(e) => {
                                    const newItems = [...(block.galleryItems || [])];
                                    newItems[idx].image = e.target.value;
                                    updateBlock(block.id, { galleryItems: newItems });
                                  }}
                                />
                                <div className="d-flex gap-2">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Caption (Optional)"
                                    value={item.caption || ''}
                                    onChange={(e) => {
                                      const newItems = [...(block.galleryItems || [])];
                                      newItems[idx].caption = e.target.value;
                                      updateBlock(block.id, { galleryItems: newItems });
                                    }}
                                  />
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Link URL (Optional)"
                                    value={item.link || ''}
                                    onChange={(e) => {
                                      const newItems = [...(block.galleryItems || [])];
                                      newItems[idx].link = e.target.value;
                                      updateBlock(block.id, { galleryItems: newItems });
                                    }}
                                  />
                                </div>
                              </div>
                              <button 
                                className="btn btn-icon btn-sm btn-light-danger"
                                onClick={() => {
                                  const newItems = [...(block.galleryItems || [])];
                                  newItems.splice(idx, 1);
                                  updateBlock(block.id, { galleryItems: newItems });
                                }}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          ))}
                        </div>

                        <div className="d-flex justify-content-center align-items-center gap-2 mt-2">
                          <div className="position-relative overflow-hidden btn btn-sm btn-light-primary">
                            Upload Images (Multiple)
                            <input 
                              type="file" 
                              className="position-absolute top-0 end-0 opacity-0 cursor-pointer h-100 w-100" 
                              accept="image/*"
                              multiple
                              onChange={(e) => {
                                handleMultipleImageUpload(e, (urls) => {
                                  const newItems = [...(block.galleryItems || [])];
                                  urls.forEach(url => newItems.push({ image: url, caption: '' }));
                                  updateBlock(block.id, { galleryItems: newItems });
                                });
                                e.target.value = ''; // Reset file input
                              }}
                            />
                          </div>
                          <button 
                            className="btn btn-sm btn-light"
                            onClick={() => {
                              const newItems = [...(block.galleryItems || [])];
                              newItems.push({ image: '', caption: '' });
                              updateBlock(block.id, { galleryItems: newItems });
                            }}
                          >
                            Add from URL
                          </button>
                        </div>
                        <div className="text-muted fs-7 mt-3 text-center">
                          Upload large images for a beautiful gallery carousel. You can select multiple files at once.
                        </div>
                      </>
                    )}
                  </div>
                )}

                {block.type === 'wysiwyg' && (
                  <div className="d-flex flex-column gap-2">
                    {isPreviewMode ? (
                      <div dangerouslySetInnerHTML={{ __html: block.content || '' }} />
                    ) : (
                      <ReactQuill
                        theme="snow"
                        value={block.content || ''}
                        onChange={(content) => updateBlock(block.id, { content })}
                        modules={QUILL_MODULES}
                        formats={QUILL_FORMATS}
                        style={{ height: '200px', marginBottom: '40px' }}
                      />
                    )}
                  </div>
                )}

                {block.type === 'featureCards' && (
                  <div className="d-flex flex-column gap-5">
                    {isPreviewMode ? (
                      <div className="row g-5">
                        {(block.cards || []).map((card, idx) => (
                          <div key={idx} className="col-12 col-md-4">
                            <div className={`card h-100 rounded-3 overflow-hidden d-flex flex-column ${card.textColor === 'black' ? 'text-dark' : 'text-white'}`} style={{ backgroundColor: card.backgroundColor || '#121E5C' }}>
                              {card.imageUrl && (
                                <img src={card.imageUrl} alt={card.title} className="w-100 object-cover" style={{ height: '200px' }} />
                              )}
                              <div className="p-6 d-flex flex-column flex-grow-1">
                                <h4 className={`fw-bolder mb-4 ${card.textColor === 'black' ? 'text-dark' : 'text-white'}`}>{card.title}</h4>
                                <p className="mb-6 opacity-75 fs-7" style={{ lineHeight: '1.375', whiteSpace: 'pre-wrap' }}>{card.description}</p>
                                <div className="mt-auto d-flex flex-column gap-3">
                                  {(card.links || []).map((link, lIdx) => (
                                    <div key={lIdx} className="d-flex align-items-center gap-2 fw-bold" style={{ cursor: 'pointer' }}>
                                      {link.text} <i className={`bi bi-arrow-right ${card.textColor === 'black' ? 'text-dark' : 'text-white'}`}></i>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        <div className="text-center mb-5">
                          <h4 className="text-gray-800 fw-bolder mb-3">Feature Cards</h4>
                          <p className="text-muted fs-7">Cards are displayed in a responsive grid. Images appear at the top.</p>
                        </div>
                        
                        <div className="d-flex flex-column gap-5">
                          {(block.cards || []).map((card, idx) => (
                            <div key={idx} className="card bg-light border p-5">
                              <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="mb-0">Card {idx + 1}</h5>
                                <button className="btn btn-icon btn-sm btn-light-danger" onClick={() => {
                                  const newCards = [...(block.cards || [])];
                                  newCards.splice(idx, 1);
                                  updateBlock(block.id, { cards: newCards });
                                }}>
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                              
                              <div className="row g-4">
                                <div className="col-md-6 d-flex flex-column gap-3">
                                  <input type="text" className="form-control" placeholder="Card Title" value={card.title || ''} onChange={(e) => {
                                    const newCards = [...(block.cards || [])];
                                    newCards[idx].title = e.target.value;
                                    updateBlock(block.id, { cards: newCards });
                                  }} />
                                  <textarea className="form-control" rows={3} placeholder="Description" value={card.description || ''} onChange={(e) => {
                                    const newCards = [...(block.cards || [])];
                                    newCards[idx].description = e.target.value;
                                    updateBlock(block.id, { cards: newCards });
                                  }} />
                                  <div className="d-flex gap-3">
                                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                                      <label className="fw-bold fs-7 mb-0 flex-shrink-0">Background:</label>
                                      <div className="input-group input-group-sm w-100">
                                        <Dropdown>
                                          <Dropdown.Toggle variant="light" className="p-0 border form-control d-flex align-items-center justify-content-center" style={{ width: '50px', height: '38px', borderTopRightRadius: 0, borderBottomRightRadius: 0 }} id={`dropdown-color-${idx}`}>
                                            <div style={{ backgroundColor: card.backgroundColor || '#121E5C', width: '30px', height: '24px', borderRadius: '4px' }}></div>
                                          </Dropdown.Toggle>
                                          <Dropdown.Menu className="p-3 shadow-sm" style={{ minWidth: '200px' }}>
                                            <div className="d-flex flex-wrap gap-2 mb-3">
                                              {['#E5832E', '#F6F6F6', '#121E5C', '#417ECC'].map(color => (
                                                <div 
                                                  key={color}
                                                  className="rounded border shadow-sm"
                                                  style={{ width: '32px', height: '32px', backgroundColor: color, cursor: 'pointer' }}
                                                  onClick={() => {
                                                    const newCards = [...(block.cards || [])];
                                                    newCards[idx].backgroundColor = color;
                                                    if (color === '#F6F6F6') newCards[idx].textColor = 'black';
                                                    else newCards[idx].textColor = 'white';
                                                    updateBlock(block.id, { cards: newCards });
                                                  }}
                                                  title={`Use ${color}`}
                                                />
                                              ))}
                                            </div>
                                            <div className="dropdown-divider mb-2"></div>
                                            <div className="position-relative d-flex align-items-center gap-2 p-2 rounded cursor-pointer" style={{ backgroundColor: '#f8f9fa' }}>
                                              <span className="fw-bold fs-7 text-dark w-100 text-center" style={{ pointerEvents: 'none' }}>Pick another color</span>
                                              <input type="color" className="position-absolute top-0 start-0 w-100 h-100 opacity-0" style={{ cursor: 'pointer' }} value={card.backgroundColor || '#121E5C'} onChange={(e) => {
                                                const newCards = [...(block.cards || [])];
                                                newCards[idx].backgroundColor = e.target.value;
                                                updateBlock(block.id, { cards: newCards });
                                              }} />
                                            </div>
                                          </Dropdown.Menu>
                                        </Dropdown>
                                        <input type="text" className="form-control" style={{ height: '38px' }} placeholder="#HexCode" value={card.backgroundColor || '#121E5C'} onChange={(e) => {
                                          const newCards = [...(block.cards || [])];
                                          newCards[idx].backgroundColor = e.target.value;
                                          updateBlock(block.id, { cards: newCards });
                                        }} />
                                      </div>
                                    </div>
                                    <div className="d-flex align-items-start gap-2 flex-grow-1">
                                      <label className="fw-bold fs-7 mb-0 flex-shrink-0 mt-2">Text:</label>
                                      <select className="form-select" value={card.textColor || 'white'} onChange={(e) => {
                                        const newCards = [...(block.cards || [])];
                                        newCards[idx].textColor = e.target.value as 'white' | 'black';
                                        updateBlock(block.id, { cards: newCards });
                                      }}>
                                        <option value="white">White</option>
                                        <option value="black">Black</option>
                                      </select>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-6 d-flex flex-column gap-3">
                                  <div className="d-flex gap-2 align-items-center">
                                    <input type="text" className="form-control" placeholder="Image URL" value={card.imageUrl || ''} onChange={(e) => {
                                      const newCards = [...(block.cards || [])];
                                      newCards[idx].imageUrl = e.target.value;
                                      updateBlock(block.id, { cards: newCards });
                                    }} />
                                    <div className="position-relative overflow-hidden btn btn-sm btn-light-primary text-nowrap">
                                      Upload
                                      <input type="file" className="position-absolute top-0 end-0 opacity-0 cursor-pointer h-100 w-100" accept="image/*" onChange={(e) => {
                                        handleImageUpload(e, (url) => {
                                          const newCards = [...(block.cards || [])];
                                          newCards[idx].imageUrl = url;
                                          updateBlock(block.id, { cards: newCards });
                                        });
                                      }} />
                                    </div>
                                  </div>
                                  {card.imageUrl && <img src={card.imageUrl} alt="preview" className="img-fluid rounded border" style={{ maxHeight: '100px', objectFit: 'cover' }} />}
                                </div>
                                
                                <div className="col-12 mt-4">
                                  <label className="fw-bold mb-2">Links</label>
                                  <div className="d-flex flex-column gap-2 mb-2">
                                    {(card.links || []).map((link, lIdx) => (
                                      <div key={lIdx} className="d-flex gap-2 align-items-center">
                                        <input type="text" className="form-control form-control-sm" placeholder="Link Text" value={link.text} onChange={(e) => {
                                          const newCards = [...(block.cards || [])];
                                          newCards[idx].links[lIdx].text = e.target.value;
                                          updateBlock(block.id, { cards: newCards });
                                        }} />
                                        <input type="text" className="form-control form-control-sm" placeholder="URL" value={link.url} onChange={(e) => {
                                          const newCards = [...(block.cards || [])];
                                          newCards[idx].links[lIdx].url = e.target.value;
                                          updateBlock(block.id, { cards: newCards });
                                        }} />
                                        <button className="btn btn-icon btn-sm btn-light-danger" onClick={() => {
                                          const newCards = [...(block.cards || [])];
                                          newCards[idx].links.splice(lIdx, 1);
                                          updateBlock(block.id, { cards: newCards });
                                        }}>
                                          <i className="bi bi-x"></i>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                  <button className="btn btn-sm btn-light-primary" onClick={() => {
                                    const newCards = [...(block.cards || [])];
                                    if (!newCards[idx].links) newCards[idx].links = [];
                                    newCards[idx].links.push({ text: 'Learn more', url: '' });
                                    updateBlock(block.id, { cards: newCards });
                                  }}>
                                    <i className="bi bi-plus"></i> Add Link
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="d-flex justify-content-center mt-4">
                          <button className="btn btn-light-primary" onClick={() => {
                            const newCards = [...(block.cards || [])];
                            newCards.push({ id: Math.random().toString(36).substring(7), title: '', description: '', imageUrl: '', backgroundColor: '#121E5C', textColor: 'white', links: [] });
                            updateBlock(block.id, { cards: newCards });
                          }}>
                            <i className="bi bi-plus-lg me-2"></i> Add Card
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {block.type === 'team' && (
                  <div className="d-flex flex-column gap-5">
                    {isPreviewMode ? (
                      <div className="d-flex flex-column w-100">
                        {(block.title || block.subtitle) && (
                          <div className="mb-10 text-center">
                            {block.title && <h2 className="fs-1 fw-bolder mb-3">{block.title}</h2>}
                            {block.subtitle && <p className="text-muted fs-4">{block.subtitle}</p>}
                          </div>
                        )}
                        <div className="d-flex flex-wrap gap-5 justify-content-center">
                          {(block.teamItems || []).map((member, idx) => (
                            <div key={idx} className="position-relative rounded-3 overflow-hidden" style={{ width: '250px', height: '250px' }}>
                              <img src={member.image || 'https://via.placeholder.com/250'} alt={member.name} className="w-100 h-100" style={{ objectFit: 'cover' }} />
                              <div className="position-absolute bottom-0 start-0 w-100 p-4 d-flex flex-column justify-content-end" style={{ height: '50%', background: 'linear-gradient(to bottom, transparent, #417ECC)' }}>
                                <h4 className="text-white fw-bold mb-1 fs-5">{member.name || 'Member Name'}</h4>
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-white opacity-75 fs-7">{member.designation || 'Role / Title'}</span>
                                  {member.link && <i className="bi bi-linkedin text-white fs-5"></i>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="d-flex flex-column gap-4">
                          <input
                            type="text"
                            className="form-control form-control-lg fw-bold"
                            placeholder="Section Title (e.g. Board of Directors)"
                            value={block.title || ''}
                            onChange={(e) => updateBlock(block.id, { title: e.target.value })}
                          />
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Optional Subtitle"
                            value={block.subtitle || ''}
                            onChange={(e) => updateBlock(block.id, { subtitle: e.target.value })}
                          />
                        </div>
                        
                        <div className="d-flex flex-column gap-4 mt-5">
                          {(block.teamItems || []).map((member, idx) => (
                            <div key={idx} className="card bg-light border p-5 d-flex flex-row gap-5">
                              <div className="d-flex flex-column justify-content-center align-items-center bg-white border rounded" style={{ width: '120px', height: '120px', overflow: 'hidden' }}>
                                {member.image ? (
                                  <img src={member.image} alt="Preview" className="w-100 h-100" style={{ objectFit: 'cover' }} />
                                ) : (
                                  <i className="bi bi-person text-muted" style={{ fontSize: '3rem' }}></i>
                                )}
                              </div>
                              <div className="d-flex flex-column flex-grow-1 gap-3">
                                <div className="row">
                                  <div className="col-md-6">
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Full Name"
                                      value={member.name}
                                      onChange={(e) => {
                                        const newItems = [...(block.teamItems || [])];
                                        newItems[idx].name = e.target.value;
                                        updateBlock(block.id, { teamItems: newItems });
                                      }}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Title / Designation"
                                      value={member.designation}
                                      onChange={(e) => {
                                        const newItems = [...(block.teamItems || [])];
                                        newItems[idx].designation = e.target.value;
                                        updateBlock(block.id, { teamItems: newItems });
                                      }}
                                    />
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-md-6">
                                    <input
                                      type="file"
                                      className="form-control"
                                      accept="image/*"
                                      onChange={(e) => {
                                        handleImageUpload(e, (url) => {
                                          const newItems = [...(block.teamItems || [])];
                                          newItems[idx].image = url;
                                          updateBlock(block.id, { teamItems: newItems });
                                        });
                                      }}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="LinkedIn URL (Optional)"
                                      value={member.link}
                                      onChange={(e) => {
                                        const newItems = [...(block.teamItems || [])];
                                        newItems[idx].link = e.target.value;
                                        updateBlock(block.id, { teamItems: newItems });
                                      }}
                                    />
                                  </div>
                                </div>
                                <div className="row mt-3">
                                  <div className="col-12">
                                    <textarea
                                      className="form-control"
                                      placeholder="Short Bio (Optional)"
                                      rows={3}
                                      value={member.bio || ''}
                                      onChange={(e) => {
                                        const newItems = [...(block.teamItems || [])];
                                        newItems[idx].bio = e.target.value;
                                        updateBlock(block.id, { teamItems: newItems });
                                      }}
                                    ></textarea>
                                  </div>
                                </div>
                              </div>
                              <button className="btn btn-icon btn-sm btn-light-danger" onClick={() => {
                                const newItems = [...(block.teamItems || [])];
                                newItems.splice(idx, 1);
                                updateBlock(block.id, { teamItems: newItems });
                              }}>
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          ))}
                          <button className="btn btn-primary align-self-start" onClick={() => {
                            const newItems = [...(block.teamItems || [])];
                            newItems.push({ name: '', designation: '', image: '', link: '', bio: '' });
                            updateBlock(block.id, { teamItems: newItems });
                          }}>
                            <i className="bi bi-plus-lg me-2"></i> Add Team Member
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            </div>
          )})
        )}
      </div>
    </div>
  );
};
